import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';

export interface AuditLogFilters {
  entity_type?: string;
  entity_id?: string;
  actor_user_id?: string;
  /** Case-insensitive substring match on actor_label, applied server-side. */
  actor_search?: string;
  date_from?: string;
  date_to?: string;
  page?: number;
  pageSize?: number;
}

export interface AuditLogEntry {
  id: string;
  created_at: string;
  actor_user_id: string | null;
  actor_label: string;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  ip_address: unknown;
  before_state: unknown;
  after_state: unknown;
  signature: string | null;
}

/** Escapes the PostgREST pattern characters in a user-supplied search term. */
function escapeLike(value: string): string {
  return value.replace(/[\\%_]/g, (m) => `\\${m}`).replace(/[,()]/g, ' ');
}

/**
 * audit_log is readable by superadmins only (RLS). Pass `enabled: false`
 * for other roles so the query does not run and error.
 */
export function useAuditLog(filters?: AuditLogFilters, opts: { enabled?: boolean } = {}) {
  const pageSize = filters?.pageSize ?? 50;
  const page = filters?.page ?? 0;

  return useQuery({
    queryKey: ['admin-audit-log', filters],
    enabled: opts.enabled ?? true,
    queryFn: async () => {
      let query = supabase
        .from('audit_log')
        .select('id, created_at, actor_user_id, actor_label, action, entity_type, entity_id, ip_address, before_state, after_state, signature', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(page * pageSize, (page + 1) * pageSize - 1);

      if (filters?.entity_type) query = query.eq('entity_type', filters.entity_type);
      if (filters?.entity_id) query = query.eq('entity_id', filters.entity_id);
      if (filters?.actor_user_id) query = query.eq('actor_user_id', filters.actor_user_id);
      if (filters?.actor_search?.trim()) query = query.ilike('actor_label', `%${escapeLike(filters.actor_search.trim())}%`);
      if (filters?.date_from) query = query.gte('created_at', filters.date_from);
      if (filters?.date_to) query = query.lte('created_at', filters.date_to);

      const { data, error, count } = await query;
      if (error) throw error;
      return { entries: (data ?? []) as unknown as AuditLogEntry[], totalCount: count ?? 0 };
    },
    staleTime: 30 * 1000,
  });
}
